// Store Page tab: which product fields the store page shows, checked ones
// first in their saved order, drag to reorder.
//
// The content type carries a default set (Catalog → Content types → Store
// Page). A product with no list of its own (an empty one, as a new product
// has) follows it: the store reads the type's (catalog product-load), so a
// change of the product's content type or of the type's defaults shows on its
// page. Ticking or moving a row here makes the list the product's own, and
// "Use <content type> Defaults" empties it again — which is why it asks first.
import React from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { Checkbox, SortableList } from "../shared/shared"
import { storePageFieldsOf } from "../shared/editor-config"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

export default function StorePageTab({ editor, contentType }) {
  const { read, set, product, loc } = editor
  const t = useProductEditorTexts()
  const ownFields = read("bookSettings.storePageFields", true) || []
  // no list of its own: the product shows (and follows) its content type's
  const followsType = ownFields.length === 0
  const currentFields = followsType ? contentType?.storePageFields || [] : ownFields
  // the rows the page may show: the fixed ones and the product's own fields
  // (its presets and whatever it carries in any language)
  const fieldsEntry = product?.fields ? loc[typeof product.fields === "object" ? product.fields?._id : product.fields] : null
  const available = storePageFieldsOf(contentType, fieldsEntry?.translations)
  const fieldMap = Object.fromEntries(available.map((f) => [f.key, f]))
  // checked fields first (in saved order), then the unchecked ones
  const orderedFields = [...currentFields.map((key) => fieldMap[key]).filter(Boolean), ...available.filter((f) => !currentFields.includes(f.key))]
  const setFields = (fields) => set("bookSettings.storePageFields", fields, true)

  const useDefaults = () =>
    showPromptDialog({
      variant: "admin",
      title: t`Use the content type's defaults?`,
      message:
        (contentType?.storePageFields || []).length === 1
          ? t("The fields chosen for this product are replaced by the %s defaults (%s field), in their order. What this product shows now is lost.", contentType?.name || t`content type`, 1)
          : t("The fields chosen for this product are replaced by the %s defaults (%s fields), in their order. What this product shows now is lost.", contentType?.name || t`content type`, (contentType?.storePageFields || []).length),
      confirmText: t`Use defaults`,
      cancelText: t`Cancel`,
      onConfirm: () => setFields([]),
    })

  const onMove = (from, to) => {
    const items = [...orderedFields]
    const [moved] = items.splice(from, 1)
    items.splice(to, 0, moved)
    setFields(items.filter((f) => currentFields.includes(f.key)).map((f) => f.key))
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Typography component="h4" variant="admin_h4" className="my-0 inline-flex items-center gap-1.5">{t`Product Fields`}<HelpHint helpKey="vendor.product.setup#store-page" /></Typography>
        {contentType && (
          <Button variant="admin_secondary" className="h-[32px] text-xs px-3" onClick={useDefaults} disabled={followsType} data-testid="use-content-type-defaults">
            {t("Use %s Defaults", contentType?.name || t`Content Type`)}
          </Button>
        )}
      </div>
      <p className="text-sm text-admin_text_secondary mt-0 mb-3">
        {t`Which fields this product's page shows, and in which order — drag to reorder. A new product starts from its content type's defaults; what is chosen here is this product's own.`}
      </p>
      <p className="text-sm text-admin_text_secondary mt-0 mb-3" data-testid="store-page-source">
        {followsType
          ? t("Follows the %s defaults: a change of the product's content type or of its defaults shows here.", contentType?.name || t`content type`)
          : t`This product's own selection: a change of its content type leaves it as it is.`}
      </p>
      <SortableList
        items={orderedFields}
        keyOf={(f) => f.key}
        onMove={onMove}
        itemClassName="mb-1"
        render={(field, index, handle) => {
          const checked = currentFields.includes(field.key)
          return (
            <div className="flex items-center gap-2 p-2 rounded-md border border-transparent hover:border-admin_bordered" data-testid={`store-field-${field.key}`}>
              {handle}
              <Checkbox checked={checked} onChange={(v) => setFields(v ? [...currentFields, field.key] : currentFields.filter((f) => f !== field.key))} label={`${t(field.label)} (${field.defaultValue})`} />
            </div>
          )
        }}
      />
    </div>
  )
}
